import {
    Alert,
    Box,
    Button,
    Divider,
    IconButton,
    List,
    ListItem,
    ListItemIcon,
    ListItemText,
    Popover,
    Stack,
    Tab,
    Tabs,
    TextField,
    Tooltip,
    Typography,
} from "@mui/material";
import {
    getMissionLocalFolderPath,
    getMissionLocalFolderPathFromRelations,
} from "./getMissionLocalFolderPath";

import ContentCopyIcon from "@mui/icons-material/ContentCopy";
import { MODEL_PROPTYPES } from "../../models";
import { PipelineInstanceDownloader } from "./PipelineInstanceDownloader";
import PropTypes from "prop-types";
import { TabPanel } from "../../components";
import { camelCaseToTitleCase } from "../../utils";
import { generateCommand } from "../../utils/generateCommand";
import { useSnackbar } from "../../hooks";
import { useState } from "react";

DownloadProjectButton.propTypes = {
    pipelineInstance: PropTypes.shape(MODEL_PROPTYPES.PipelineInstance),
    mission: PropTypes.shape(MODEL_PROPTYPES.Mission).isRequired,
};

export function DownloadProjectButton({ pipelineInstance, mission }) {
    const [anchorEl, setAnchorEl] = useState(null);

    const handleClick = (event) => setAnchorEl(event.currentTarget);

    const handleClose = () => setAnchorEl(null);

    const open = Boolean(anchorEl);

    return (
        <>
            <Button variant="contained" onClick={handleClick}>
                Download Project
            </Button>
            <Popover
                open={open}
                anchorEl={anchorEl}
                onClose={handleClose}
                anchorOrigin={{
                    vertical: "bottom",
                    horizontal: "left",
                }}
                sx={{ maxWidth: "600px" }}
            >
                {pipelineInstance ? (
                    <DownloadProjectTabs
                        pipelineInstance={pipelineInstance}
                        mission={mission}
                    />
                ) : (
                    <DownloadProjectNoPipeline mission={mission} />
                )}
            </Popover>
        </>
    );
}

DownloadProjectTabs.propTypes = {
    pipelineInstance: PropTypes.shape(MODEL_PROPTYPES.PipelineInstance)
        .isRequired,
    mission: PropTypes.shape(MODEL_PROPTYPES.Mission).isRequired,
};
function DownloadProjectTabs({ pipelineInstance, mission }) {
    const [value, setValue] = useState(0);

    const handleChange = (_, newValue) => {
        setValue(newValue);
    };

    const pipelineInstanceDownloader = new PipelineInstanceDownloader(
        pipelineInstance
    );

    const outputFolder =
        getMissionLocalFolderPath(mission, pipelineInstance) ?? ".";

    return (
        <Box sx={{ p: 1 }}>
            <Typography variant="subtitle1" sx={{ p: 1 }}>
                Select your task. Then copy the command to launch in{" "}
                <Box
                    sx={{
                        display: "inline",
                        p: 0.5,
                        borderRadius: 1,
                        bgcolor: (theme) => theme.palette.grey[210],
                    }}
                >
                    cmd
                </Box>
                .
            </Typography>

            <Tabs value={value} onChange={handleChange}>
                <Tab label="To do extraction" />
                <Tab label="To redo photogrammetry" />
            </Tabs>

            <TabPanel value={value} index={0} boxStyle={{ p: 1 }}>
                <DownloadCommandPanel
                    itemsToDownload={
                        pipelineInstanceDownloader.itemsForExtraction
                    }
                    outputFolder={outputFolder}
                />
            </TabPanel>

            <TabPanel value={value} index={1} boxStyle={{ p: 1 }}>
                <DownloadCommandPanel
                    itemsToDownload={
                        pipelineInstanceDownloader.itemsForPhotogrammetry
                    }
                    outputFolder={outputFolder}
                />
            </TabPanel>
        </Box>
    );
}

DownloadProjectNoPipeline.propTypes = {
    mission: PropTypes.shape(MODEL_PROPTYPES.Mission).isRequired,
};
function DownloadProjectNoPipeline({ mission }) {
    const itemsToDownload = {
        images: {
            kind: "downloadFiles",
            endpoint: "/raw-datasets",
            args: { missionUuid: mission.uuid },
            from: "s3Location",
            to: "destination",
        },
        sessionMetadataJson: {
            kind: "buildSessionMetadata",
            missionUuid: mission.uuid,
            to: "session-metadata.json",
        },
    };

    const outputFolder = getMissionLocalFolderPathFromRelations(mission) ?? ".";

    return (
        <Box sx={{ p: 1 }}>
            <Typography variant="subtitle1" sx={{ p: 1 }}>
                Select your task. Then copy the command to launch in{" "}
                <Box
                    sx={{
                        display: "inline",
                        p: 0.5,
                        borderRadius: 1,
                        bgcolor: (theme) => theme.palette.grey[210],
                    }}
                >
                    cmd
                </Box>
                .
            </Typography>
            <DownloadCommandPanel
                itemsToDownload={itemsToDownload}
                outputFolder={outputFolder}
            />
        </Box>
    );
}

DownloadCommandPanel.propTypes = {
    itemsToDownload: PropTypes.object.isRequired,
    outputFolder: PropTypes.string.isRequired,
};
export function DownloadCommandPanel({ itemsToDownload, outputFolder }) {
    const { openSnackbar } = useSnackbar();

    // We need to add outputFolder
    const preExecSpecs = {
        ...itemsToDownload,
        outputFolder,
    };
    const command = generateCommand(preExecSpecs, "pre");

    const onClickCopy = async () => {
        try {
            await navigator.clipboard.writeText(command);
        } catch (error) {
            openSnackbar(error.message, "error");
        }
    };

    return (
        <Stack direction="column" spacing={1}>
            <Stack direction="row" spacing={1}>
                <TextField
                    defaultValue={command}
                    variant="outlined"
                    InputProps={{
                        readOnly: true,
                        sx: { bgcolor: (theme) => theme.palette.grey[210] },
                    }}
                    fullWidth
                />
                <Tooltip title="Copy">
                    <IconButton onClick={onClickCopy}>
                        <ContentCopyIcon />
                    </IconButton>
                </Tooltip>
            </Stack>
            <Divider />
            <Box>
                <Typography>Command will download (if available)</Typography>
                <List>
                    {Object.keys(itemsToDownload)
                        .toSorted()
                        .map((name) => (
                            <ListItem key={name} disablePadding>
                                <ListItemIcon>&bull;</ListItemIcon>
                                <ListItemText
                                    primary={camelCaseToTitleCase(name)}
                                />
                            </ListItem>
                        ))}
                </List>
                <Typography>to</Typography>
                <Box
                    sx={{
                        overflowWrap: "anywhere",
                        p: 1,
                        fontStyle: outputFolder === "." ? "italic" : "normal",
                    }}
                >
                    {outputFolder === "." ? "the current folder" : outputFolder}
                </Box>
                {outputFolder === "." && (
                    <Alert severity="warning">
                        Automatic folder tree unavailable. Go to the appropriate
                        folder before launching the command.
                    </Alert>
                )}
            </Box>
        </Stack>
    );
}
